import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Runs an async function and reports { data, loading, error, reload }.
 *
 * Every data hook in the landing and auth modules sits on top of this one, so
 * the three-state contract and the error wording stay identical across both —
 * which is what services buy us by throwing instead of formatting errors.
 *
 * @param {Function} task    Called with no arguments; must return a promise.
 * @param {Array}    deps    Re-runs the task when these change, like useEffect.
 * @param {*}        initial Value for `data` before the first result arrives.
 */
export const useAsync = (task, deps = [], initial = null) => {
    const { t } = useTranslation();
    const [state, setState] = useState({ data: initial, loading: true, error: null });

    // A late response from a superseded request must not overwrite a newer one,
    // and must not set state after the component is gone.
    const runIdRef = useRef(0);

    const run = useCallback(() => {
        const runId = ++runIdRef.current;
        setState(prev => ({ ...prev, loading: true, error: null }));

        return task()
            .then(data => {
                if (runId !== runIdRef.current) return;
                setState({ data, loading: false, error: null });
            })
            .catch(err => {
                if (runId !== runIdRef.current) return;
                // `err.response` means the server answered and refused;
                // its absence means the request never got there.
                const error = err?.response ? t('ErrorBody') : t('ErrorConn');
                setState({ data: initial, loading: false, error });
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    useEffect(() => {
        run();
        // Invalidate any in-flight run on unmount.
        return () => { runIdRef.current += 1; };
    }, [run]);

    return { ...state, reload: run };
};
